import { useEffect, useState } from 'react';
import { StyleSheet, css } from 'aphrodite';

import { useTranslation } from 'react-i18next';
import {
  clearMapEvent,
  restartDrawPropertyEvent,
  polygonDrawnEvent,
  centerBoundingBoxEvent,
} from '../map/MapComponent';
import { EventBus } from 'ts-bus';
import { LatLng } from '../../server/model/property/LatLng';
import { getAreaSizeInGivenAreaType } from '../../util/MapUtil';
import {
  UserSettings,
  getReadableSize,
  AreaType,
} from '../../server/model/user/UserSettings';
import {
  Button,
} from '@mui/material';
import { defaultStyles } from '../../styling/styles';
import { SECONDARY_TEXT_COLOR_ON_WHITE } from '../../styling/colors';
import { TEXT_SIZE_SMALL } from '../../styling/dimens';
import GeocoderAutoComplete, { GeocodeResult } from './GeocoderAutoComplete';

export type AddNewPropertyViaDrawComponentProps = {
  eventBus: EventBus;
  sendingCreatePropertyRequest: boolean;
  userSettings: UserSettings;
};

let subscribedEvents: { (): void }[] = [];

export default function AddNewPropertyViaDrawComponent(
  addNewPropertyViaDrawComponentProps: AddNewPropertyViaDrawComponentProps
) {
  const [coordinates, setCoordinates] = useState<LatLng[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    subscribedEvents.push(
      addNewPropertyViaDrawComponentProps.eventBus.subscribe(
        polygonDrawnEvent,
        (event) => {
          const polygonCoordinates = event.payload.coordinates;
          setCoordinates(polygonCoordinates);
        }
      )
    );

    return () => {
      subscribedEvents.forEach((subscribedEvent) => subscribedEvent());
      subscribedEvents = [];
    };
  }, [addNewPropertyViaDrawComponentProps.eventBus]);

  // At start clear once the map
  useEffect(() => {
    addNewPropertyViaDrawComponentProps.eventBus.publish(clearMapEvent({}));
    addNewPropertyViaDrawComponentProps.eventBus.publish(restartDrawPropertyEvent({}));
  }, [addNewPropertyViaDrawComponentProps.eventBus]);

  const calculatedSize = getAreaSizeInGivenAreaType(coordinates, addNewPropertyViaDrawComponentProps.userSettings.preferredAreaType);
  const userReadableCalculatedSize = getReadableSize(
    calculatedSize,
    addNewPropertyViaDrawComponentProps.userSettings.preferredAreaType,
    t
  );

  return (
    <div>
      <div
        dangerouslySetInnerHTML={{
          __html: t('PROPERTIES.ADD_PROPERTY.DRAW_INSTRUCTIONS'),
        }}
        className={css(styles.caption)}
      />
      <div className={css(styles.smallSpacing)} />
      <div className={css(styles.smallSpacing)} />
      <GeocoderAutoComplete
        disabled={addNewPropertyViaDrawComponentProps.sendingCreatePropertyRequest}
        clickedGeocodeResult={(geocodeResult: GeocodeResult) => {
          addNewPropertyViaDrawComponentProps.eventBus.publish(
            centerBoundingBoxEvent({
              switchToMapViewOnMobileDevice: true,
              upperLeft: geocodeResult.boundingBox.upperLeft,
              lowerRight: geocodeResult.boundingBox.lowerRight,
            })
          );
        }}
      />
      <div className={css(styles.smallSpacing)} />
      <div className={css(styles.smallSpacing)} />
      <div className={css(styles.caption)}>
        {t('PROPERTIES.ADD_PROPERTY.CALCULATED_SIZE') +
          (calculatedSize === 0
            ? t('PROPERTIES.ADD_PROPERTY.NO_DRAWN_PROPERTY')
            : userReadableCalculatedSize)}
      </div>
      <div className={css(styles.smallSpacing)} />
      <div className={css(styles.smallSpacing)} />
      <span
        onClick={() => {
          if (!addNewPropertyViaDrawComponentProps.sendingCreatePropertyRequest) {
            setCoordinates([]);
            addNewPropertyViaDrawComponentProps.eventBus.publish(
              restartDrawPropertyEvent({})
            );
          }
        }}
      >
        <Button
          className={css(styles.smallButton)}
          disabled={
            addNewPropertyViaDrawComponentProps.sendingCreatePropertyRequest || coordinates.length === 0
          }
          variant="contained"
          color="primary"
        >
          {t('PROPERTIES.ADD_PROPERTY.RESTART_DRAWING')}
        </Button>
      </span>
      <div className={css(styles.spacing)} />
    </div>
  );
}

const styles = StyleSheet.create({
  
  caption: {
    ...defaultStyles.textStyle,
    fontSize: TEXT_SIZE_SMALL,
    color: SECONDARY_TEXT_COLOR_ON_WHITE,
    alignItems: 'center',
  },
  smallSpacing: {
    height: 10,
  },
  spacing: {
    height: 35,
  },
  smallButton: {
    ...defaultStyles.materialSmallButtonStyle,
    fontWeight: 'bold',
    float: 'right',
  },
});
